import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const cardBase =
  "rounded-xl p-5 flex flex-col gap-3 border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022] hover:shadow-[0_0_20px_#00ff9044] transition-shadow";

const STATUS_COLORS = {
  open: "#f87171",
  in_progress: "#fbbf24",
  resolved: "#00ff90",
  closed: "#60a5fa",
  pending: "#c084fc",
};

const TicketTrackerPanel = ({ managerId }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/manager/tickets?manager_id=${managerId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (!res.ok) throw new Error("Failed to load tickets"); return res.json(); })
      .then((data) => setTickets(data.tickets || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [managerId]);

  const statusMap = tickets.reduce((acc, t) => {
    const s = t.status || "unknown";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const chartData = Object.entries(statusMap).map(([status, count]) => ({
    name: status.replace("_", " "),
    value: count,
    color: STATUS_COLORS[status] || "#ffffff33",
  }));

  return (
    <div className={cardBase}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest">Team Tickets</span>
        <span className="text-2xl">🎫</span>
      </div>

      {loading && <p className="text-white/20 text-sm animate-pulse">Loading...</p>}
      {error && <p className="text-red-400 text-sm">⚠ {error}</p>}

      {!loading && !error && (
        <>
          {tickets.length > 0 && (
            <div className="text-2xl font-bold text-white">
              {tickets.length} <span className="text-base font-normal text-white/30">total tickets</span>
            </div>
          )}

          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={chartData} cx="50%" cy="50%" innerRadius={42} outerRadius={60} paddingAngle={3} dataKey="value">
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} fillOpacity={0.85} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 }}
                  labelStyle={{ color: "#00ff90" }}
                  formatter={(val, name) => [`${val} ticket${val !== 1 ? "s" : ""}`, name]}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }} formatter={(v) => <span style={{ textTransform: "capitalize" }}>{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-white/20 text-sm">No active tickets.</p>
          )}

          {tickets.length > 0 && (
            <ul className="text-sm space-y-1 max-h-24 overflow-y-auto pr-1">
              {tickets.slice(0, 4).map((t, i) => (
                <li key={i} className="flex items-center justify-between gap-2">
                  <span className="text-white/50 truncate text-xs">{t.title}</span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full capitalize shrink-0"
                    style={{
                      background: `${STATUS_COLORS[t.status] || "#ffffff"}15`,
                      color: STATUS_COLORS[t.status] || "rgba(255,255,255,0.3)",
                      border: `1px solid ${STATUS_COLORS[t.status] || "#ffffff"}30`,
                    }}
                  >
                    {t.status?.replace("_", " ") || "unknown"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default TicketTrackerPanel;